/**
 * Ustawienia (Faza 4): godziny i progi ceremonii, tryby narzędzi,
 * powiadomienia push i profil pamięci. Faza 5 dokłada Google Calendar,
 * Faza 7 stan capture z e-maila, Faza 8 zużycie modelu oraz eksport/backup.
 *
 * Wszystko idzie przez REST (a nie przez agenta) - to ekran serwisowy.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { CalendarSection } from "./CalendarSection";
import { ExportSection } from "./ExportSection";
import { LlmUsageSection } from "./LlmUsageSection";
import { EmailSection } from "./EmailSection";
import { CeremonyCard } from "./CeremonyCard";
import { PushSection } from "./PushSection";
import { MemorySection } from "./MemorySection";
import type { CeremonyConfig, ToolMode } from "./types";

export const settingsKeys = {
  ceremonies: ["ceremonies"] as const,
  toolModes: ["tool-modes"] as const,
  memory: ["memory"] as const,
  pushConfig: ["push-config"] as const,
  calendar: ["calendar-status"] as const,
  email: ["email-status"] as const,
  googleCalendars: ["google-calendars"] as const,
  backups: ["backups"] as const,
  llmUsage: ["llm-usage"] as const,
};

export function SettingsPage() {
  return (
    <div className="view settings">
      <CeremoniesSection />
      <ToolModesSection />
      <CalendarSection />
      <EmailSection />
      <PushSection />
      <MemorySection />
      <LlmUsageSection />
      <ExportSection />
    </div>
  );
}

// --------------------------------------------------------------------------
// Ceremonie
// --------------------------------------------------------------------------

function CeremoniesSection() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKeys.ceremonies,
    queryFn: api.ceremonies,
  });
  const [drafts, setDrafts] = useState<Record<string, CeremonyConfig>>({});

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.saveSettings(values),
    onSuccess: () => {
      setDrafts({});
      void qc.invalidateQueries({ queryKey: settingsKeys.ceremonies });
    },
  });

  const dirty = Object.keys(drafts).length > 0;

  function onChange(settingKey: string, config: CeremonyConfig) {
    setDrafts((prev) => ({ ...prev, [settingKey]: config }));
  }

  return (
    <section className="card settings__section">
      <header className="card__head">
        <h2 className="card__title">Ceremonie</h2>
        <button
          className="btn btn--primary btn--small"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(drafts)}
        >
          {save.isPending ? "Zapisuję…" : "Zapisz"}
        </button>
      </header>
      <p className="muted">
        Godziny i progi. Zmiana przeładowuje harmonogram od razu — bez restartu backendu.
      </p>

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać ceremonii.</p>}
      {save.error && <p className="error">Nie udało się zapisać ustawień.</p>}

      <ul className="rows">
        {(data ?? []).map((ceremony) => (
          <CeremonyCard
            key={ceremony.name}
            ceremony={ceremony}
            draft={drafts[ceremony.setting_key]}
            onChange={(config) => onChange(ceremony.setting_key, config)}
          />
        ))}
      </ul>
    </section>
  );
}

// --------------------------------------------------------------------------
// Tryby narzędzi
// --------------------------------------------------------------------------

const MODE_HINT: Record<ToolMode, string> = {
  auto: "wykonuje od razu",
  confirm: "pyta o zgodę",
};

function ToolModesSection() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKeys.toolModes,
    queryFn: api.toolModes,
  });
  const save = useMutation({
    mutationFn: (modes: Record<string, ToolMode>) => api.setToolModes(modes),
    onSuccess: (modes) => qc.setQueryData(settingsKeys.toolModes, modes),
  });

  const entries = Object.entries(data ?? {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <section className="card settings__section">
      <h2 className="card__title">Narzędzia agenta</h2>
      <p className="muted">
        <code>confirm</code> — agent proponuje i czeka na kliknięcie. <code>auto</code> — wykonuje
        od razu, z przyciskiem „cofnij”.
      </p>

      {isLoading && <p className="muted">Ładowanie…</p>}
      {(error || save.error) && <p className="error">Nie udało się zapisać trybów.</p>}

      <ul className="rows">
        {entries.map(([tool, mode]) => (
          <li className="row" key={tool}>
            <div className="row__main">
              <span className="row__title">{tool}</span>
              <span className="row__meta">{MODE_HINT[mode]}</span>
            </div>
            <div className="row__actions">
              <button
                className={`chip ${mode === "confirm" ? "chip--active" : ""}`}
                disabled={save.isPending}
                onClick={() => save.mutate({ [tool]: "confirm" })}
              >
                confirm
              </button>
              <button
                className={`chip ${mode === "auto" ? "chip--active" : ""}`}
                disabled={save.isPending}
                onClick={() => save.mutate({ [tool]: "auto" })}
              >
                auto
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Wspólny input liczbowy dla configów ceremonii. */
export function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(String(value ?? ""));
  useEffect(() => setText(String(value ?? "")), [value]);

  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        className="field__input"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const parsed = Number(e.target.value);
          if (!Number.isNaN(parsed) && parsed >= min && parsed <= max) onChange(parsed);
        }}
      />
    </label>
  );
}
